import { errorMessage } from '../error';
import {
  ACCESSOR_GET_POINTER,
  ACCESSOR_MARKER,
  ACCESSOR_SET_POINTER,
  beautifyBreadcrumbs,
  beautifyPath,
  ENCODER_EMPTY_MARKER,
  ENCODER_MARKER,
  extractPointer,
  FUNCTION_MARKER,
  isFinalNode,
  isSerializableType,
  NO_ACCESSOR_INDICATOR,
  PATH_SEPARATOR,
  WITH_ACCESSOR_INDICATOR,
} from './utils';
import {
  arrayEncoder,
  mapEncoder,
  objectEncoder,
  setEncoder,
} from './encoders';

/**
 * { a:1,b:2, c:[1, {d:5}] }
 * =>
 * {
 *   type: 'object',
 *   data: [['a', 1], ['b', 2], ['#c.0', 1], ['c.#1.d', 5]]
 * }
 */
export interface ISerializedPayload {
  data: [string, string | number | boolean | null | undefined][];
}

export type IRegisterFunctionMarker = (
  path: string,
  value: (...args: any) => any,
) => void;

export type IReplaceFunctionMarker = (path: string) => (props: any[]) => any;

const encoders = [arrayEncoder, setEncoder, mapEncoder];

function getDescriptor(obj: any, key: string) {
  let _instance = obj;

  while (_instance) {
    const descriptor = Object.getOwnPropertyDescriptor(_instance, key);
    if (descriptor) {
      return descriptor;
    }

    _instance = Reflect.getPrototypeOf(_instance);
  }

  return null;
}

function forEachMember(
  root: any,
  callback: (
    breadcrumbs: string[],
    config: {
      context: {};
      pointer: string;
      value: any;
    },
  ) => void,
) {
  const ancestors = new WeakMap<any, string[]>();
  const getContextPointer = (breadcrumbs: string[]) => {
    const _breadcrumbs = breadcrumbs
      .slice(1) // TODO
      .map(extractPointer)
      .filter(Boolean);

    return _breadcrumbs.slice(1).reduce<any>(
      (acc, pointer) => {
        return {
          context: acc.context[acc.pointer],
          pointer,
        };
      },
      {
        context: root,
        pointer: _breadcrumbs[0],
      },
    );
  };

  const iterateStructure = (instance: any, breadcrumbs: string[] = []) => {
    if (ancestors.has(instance)) {
      const hasDuplicates = ancestors
        .get(instance)
        ?.some((p) => breadcrumbs.join('.').startsWith(p));

      if (hasDuplicates) {
        callback(breadcrumbs, {
          ...getContextPointer(breadcrumbs),
          value: '[Circular]',
        });
        return;
      }
    }

    if (instance && !isFinalNode(instance)) {
      if (ancestors.has(instance)) {
        ancestors.set(instance, []);
      }

      ancestors.set(instance, [
        ...(ancestors.get(instance) ?? []),
        breadcrumbs.join('.'),
      ]);
    }

    if (isFinalNode(instance)) {
      callback(breadcrumbs, {
        ...getContextPointer(breadcrumbs),
        value: instance,
      });
      return;
    }

    const encoder =
      encoders.find((_encoder) => _encoder.shouldUse(instance)) ??
      objectEncoder;

    if (encoder) {
      encoder.serialize(instance, (key, value) => {
        iterateStructure(value, [
          ...breadcrumbs,
          `${ENCODER_MARKER}${encoder.marker}`,
          key,
        ]);
      });

      return;
    }
  };

  return iterateStructure(root);
}

export const serialize = (
  input: any,
  registerFunctionMarker: IRegisterFunctionMarker,
): ISerializedPayload => {
  if (!isSerializableType(input)) {
    throw new Error(
      errorMessage('only objects are serializable at the root level'),
    );
  }

  const buffer: ISerializedPayload = {
    data: [],
  };

  const appendValue = (path: string, value: any) => {
    buffer.data.push([path, value]);
  };

  const appendFunction = (
    path: string,
    config: {
      context: any;
      pointer: string;
    },
  ) => {
    registerFunctionMarker(beautifyPath(path), (...args: any[]) => {
      return config.context[config.pointer](...args);
    });

    appendValue(path, FUNCTION_MARKER);
  };

  forEachMember(input, (breadcrumbs, { value, context, pointer }) => {
    const path = breadcrumbs.slice(1).join(PATH_SEPARATOR);

    const descriptor = getDescriptor(context, pointer);

    if (descriptor?.get || descriptor?.set) {
      if (descriptor?.get) {
        registerFunctionMarker(
          beautifyBreadcrumbs([...breadcrumbs, ACCESSOR_GET_POINTER]),
          () => (context as unknown as any)[pointer],
        );
      }
      if (descriptor?.set) {
        registerFunctionMarker(
          beautifyBreadcrumbs([...breadcrumbs, ACCESSOR_SET_POINTER]),
          (_value: any) => {
            (context as unknown as any)[pointer] = _value;
          },
        );
      }
      const marker = [
        descriptor?.get ? WITH_ACCESSOR_INDICATOR : NO_ACCESSOR_INDICATOR,
        descriptor?.set ? WITH_ACCESSOR_INDICATOR : NO_ACCESSOR_INDICATOR,
      ];

      appendValue(path, `${ACCESSOR_MARKER}${marker.join('')}`);
    } else if (typeof value === 'function') {
      appendFunction(path, { context, pointer });
    } else {
      appendValue(path, value);
    }
  });

  return buffer;
};

export const deserialize = (
  input: ISerializedPayload,
  replaceFunctionMarker: IReplaceFunctionMarker,
) => {
  if (typeof input !== 'object' || input === null) {
    return input;
  }

  return input.data.reduce<any>(
    (acc, [breadcrumbs, value]) => {
      let pointer = acc;

      const pointers = breadcrumbs.split(PATH_SEPARATOR);

      let encoder = objectEncoder as any;

      let key = null as any;

      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < pointers.length; i++) {
        const point = pointers[i]!;
        if (point[0] === ENCODER_MARKER) {
          const has = encoder.deserialize.has(pointer, key);

          encoder = [objectEncoder, ...encoders].find(
            (_iterator) => _iterator.marker === point[1],
          );

          if (!encoder) {
            throw new Error(errorMessage(`unknown marker "${point[1]}"`));
          }

          if (!has) {
            pointer[key] = encoder.deserialize.create();
          }

          pointer = pointer[key];
        } else {
          key = point;
        }
      }

      if (value?.toString() === ENCODER_EMPTY_MARKER) {
        return acc;
      } else if (value?.toString() === FUNCTION_MARKER) {
        encoder.deserialize.append(
          pointer,
          key,
          replaceFunctionMarker(beautifyBreadcrumbs(pointers)),
        );
      } else if (value?.toString()[0] === ACCESSOR_MARKER) {
        const hasGetter = value?.toString()[1] === WITH_ACCESSOR_INDICATOR;
        const hasSetter = value?.toString()[2] === WITH_ACCESSOR_INDICATOR;

        const descriptor: PropertyDescriptor = {};
        if (hasGetter) {
          const getter = replaceFunctionMarker(
            beautifyBreadcrumbs([...pointers, ACCESSOR_GET_POINTER]),
          );

          descriptor.get = () => (getter as any)();
        }

        if (hasSetter) {
          descriptor.set = replaceFunctionMarker(
            beautifyBreadcrumbs([...pointers, ACCESSOR_SET_POINTER]),
          );
        }

        Object.defineProperty(pointer, key!, descriptor);
      } else {
        encoder.deserialize.append(pointer, key, value);
      }

      return acc;
    },
    // TODO: define as enumrable
    {
      [Symbol('transferred object')]: `${(
        JSON.stringify(input.data).length / 1024
      ).toFixed(2)}Kb`,
    },
  );
};
